import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { pointsToast, startGoogleLogin, useAuth } from "@/context/AuthContext";
import { errText, forgotPassword, login, signup } from "@/lib/api";

const GoogleBtn = () => (
  <button type="button" onClick={startGoogleLogin} className="btn w-full border-[1.5px] border-ink bg-white !normal-case" data-testid="auth-google-button">
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.8 6C12.4 13.6 17.7 9.5 24 9.5z" /><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17.5z" /><path fill="#FBBC05" d="M10.5 28.7c-.5-1.4-.8-3-.8-4.7s.3-3.3.8-4.7l-7.8-6C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.8-6z" /><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.8 6C6.6 42.6 14.6 48 24 48z" /></svg>
    <span className="font-a">Continue with Google</span>
  </button>
);

const Input = ({ id, label, ...rest }) => (
  <label className="block"><span className="label text-ink/60">{label}</span><input className="field" data-testid={`auth-${id}`} {...rest} /></label>
);

export const AuthForms = () => {
  const { signIn } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "", newsletter: true });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") setSent((await forgotPassword(f.email)).message);
      else {
        const d = mode === "login" ? await login({ email: f.email, password: f.password }) : await signup(f);
        signIn(d);
        if (mode === "signup") pointsToast(d.user.points, "welcome to the crew");
        else toast.success(`Welcome back, ${d.user.name || "suspect"}.`);
      }
    } catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  return (
    <div className="mx-auto grid max-w-[1100px] gap-10 lg:grid-cols-2" data-testid="auth-forms">
      <div className="rounded-[24px] border-[1.5px] border-ink bg-cloud p-6 sm:p-10">
        <div className="flex gap-2" role="tablist">
          {[["login", "Log in"], ["signup", "Create account"]].map(([m, l]) => (
            <button key={m} role="tab" aria-selected={mode === m} onClick={() => { setMode(m); setSent(""); }} className={`label h-9 rounded-full border-[1.5px] border-ink px-5 font-bold ${mode === m ? "bg-ink text-cloud" : ""}`} data-testid={`auth-tab-${m}`}>{l}</button>
          ))}
        </div>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid={`auth-form-${mode}`}>
          {mode === "signup" && <Input id="name" label="First name" value={f.name} onChange={set("name")} required autoComplete="given-name" />}
          <Input id="email" label="Email" type="email" value={f.email} onChange={set("email")} required autoComplete="email" />
          {mode !== "forgot" && <Input id="password" label={mode === "signup" ? "Password (8+ characters)" : "Password"} type="password" value={f.password} onChange={set("password")} required minLength={mode === "signup" ? 8 : 1} autoComplete={mode === "signup" ? "new-password" : "current-password"} />}
          {mode === "signup" && (
            <label className="flex items-start gap-3 text-sm"><Checkbox checked={f.newsletter} onCheckedChange={(v) => setF({ ...f, newsletter: !!v })} data-testid="auth-newsletter" /> Join the Sus Crew newsletter: early drop access, no spam. Untick to opt out.</label>
          )}
          {sent && <p className="rounded-[12px] bg-lime px-4 py-3 text-sm" data-testid="auth-forgot-sent">{sent}</p>}
          <button disabled={busy} className="btn btn-ink w-full" data-testid="auth-submit">{busy ? "One sec…" : mode === "login" ? "Log in" : mode === "signup" ? "Create account · +25 pts" : "Send reset link"} <ArrowRight size={16} /></button>
          <div className="flex justify-between text-sm">
            {mode === "login" ? <button type="button" onClick={() => setMode("forgot")} className="link-u" data-testid="auth-forgot-link">Forgot password?</button> : <button type="button" onClick={() => setMode("login")} className="link-u" data-testid="auth-back-login">Back to log in</button>}
            <Link to="/order-lookup" className="link-u" data-testid="auth-order-lookup">Guest order lookup</Link>
          </div>
        </form>
        <div className="my-6 flex items-center gap-4"><span className="h-px flex-1 bg-ink/15" /><span className="label text-ink/50">or</span><span className="h-px flex-1 bg-ink/15" /></div>
        <GoogleBtn />
      </div>
      <div className="flex flex-col justify-center rounded-[24px] bg-pink p-8 sm:p-10">
        <p className="display text-[clamp(2.5rem,5vw,4rem)]">Crew perks</p>
        <ul className="font-a mt-6 space-y-3 text-[17px]">
          <li>+25 Crew Points just for joining</li><li>+5 pts per Sus of the Month vote</li><li>+10 pts when you share a design or product</li><li>1 pt per C$1 you spend</li><li>Trade points for discounts and crew-only stickers</li>
        </ul>
      </div>
    </div>
  );
};
